import { FC, useCallback, useMemo } from 'react';

import {
  useGetSubscription,
  useGetViewDatas,
} from 'modules/account/settings/hooks';
import { AiOutlineCaretDown } from 'react-icons/ai';
import { BsFillPencilFill } from 'react-icons/bs';
import { IoChevronBackSharp } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  Button,
  CustomQuickEditField,
  DeleteItemModal,
  IconButton,
  QuickEditAddress,
  QuickEditDropdown,
  QuickEditInput,
} from 'ui/components/common';
import FILTER_TYPES from 'ui/components/form/FilterForm/filterTypes';
import { AvatarUploader } from 'ui/components/img/Avatar';
import { NavAction } from 'ui/components/nav';
import useModal from 'ui/partials/Modal/useModal';
import c from 'utils/constants';
import { cn, validateURL } from 'utils/helpers';
import { Can } from 'utils/providers/RolePermission';
import { DATA_SOURCE, VIEWDATA_TYPE } from 'utils/types';

import { useCompanyProfileContext } from '../containers/Companies/SelectedCompany';
import { ADDRESS_FORM } from '../forms/schema';
import {
  useDeleteCompany,
  useGetCompanyContactDetails,
  useUpdateCompany,
} from '../hooks';
import UpdateCompanyModal from './UpdateCompanyModal';
import CustomFieldsModal from './CustomFieldsModal';

interface PROPS {
  id: string;
}

const CompanyContactDetails: FC<PROPS> = ({ id }) => {
  const { data: subscription } = useGetSubscription();
  const { data } = useGetCompanyContactDetails(id, {
    include: ['leads'],
  });
  const { mutate: updateCompany, isLoading } = useUpdateCompany();

  const { data: fieldsData } = useGetViewDatas({
    type: VIEWDATA_TYPE.INFO_PAGE,
    source: DATA_SOURCE.COMPANY,
    'include[0]': 'dataList',
  });

  const dataList = fieldsData?.data?.[0]?.dataList ?? [];

  const {
    companyWebsite,
    noEmployees,
    annualRevenue,
    description,
    linkedinUrl,
    industry,
    city,
    state,
    postalCode,
    type,
    data: companyCustomData,
  } = data ?? {};

  const handleUpdate = (field: string, value: any, onSuccess: () => void) => {
    const payload = { [field]: value, id } as any;

    updateCompany(payload, { onSuccess });
  };

  const handleSubmitAddress = (values: ADDRESS_FORM, onSuccess: () => void) => {
    updateCompany({ ...values, id }, { onSuccess });
  };

  return (
    <div
      className={cn(
        'border-t-[0.5px] ',
        'border-t-separator-primary ',
        'mt-5 ',
        'max-h-[calc(100vh-440px)] ',
        'overflow-y-auto ',
        'scrollbar-thin',
      )}
    >
      <QuickEditInput
        title="Company Website"
        value={companyWebsite}
        rule={(val) => {
          const valid = validateURL(val);

          if (!valid) {
            return 'Please input a valid URL';
          }
        }}
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('companyWebsite', val, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      <QuickEditDropdown
        title="Industry"
        value={industry}
        options={FILTER_TYPES.industry}
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('industry', val.value, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      <QuickEditDropdown
        title="Type"
        value={type}
        options={FILTER_TYPES.companyTypes}
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('type', val.value, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      <QuickEditInput
        title="Number of employees"
        value={noEmployees?.toString()}
        isNumber
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('noEmployees', val, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      <QuickEditInput
        title="Annual Revenue"
        value={annualRevenue?.toString()}
        isNumber
        isAmount
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('annualRevenue', val, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      <QuickEditInput
        title="Description"
        value={description}
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('description', val, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      <QuickEditAddress
        isCompany
        initialValues={{
          state,
          city,
          postalCode,
        }}
        onSubmit={handleSubmitAddress}
        title="Address"
        disabled={subscription?.isExpired}
      />
      <QuickEditInput
        title="LinkedIn Company Page"
        value={linkedinUrl}
        rule={(val) => {
          const valid = validateURL(val);

          if (!valid) {
            return 'Please input a valid URL';
          }
        }}
        isLoading={isLoading}
        onSubmit={(val, onSuccess) =>
          handleUpdate('linkedinUrl', val, onSuccess)
        }
        disabled={subscription?.isExpired}
      />
      {dataList?.map((data) => {
        const dataIdValue =
          companyCustomData?.find((d) => d.dataId === data._id)?.value ?? null;
        return (
          <CustomQuickEditField
            data={data}
            key={data._id}
            dataId={data._id}
            value={dataIdValue}
            onSubmit={(id, val, onSuccess) => {
              const newCompanyCustomData: typeof companyCustomData =
                companyCustomData?.length
                  ? companyCustomData
                      ?.map((d: any) => ({
                        dataId: d.dataId,
                        value: d.dataId === id ? val : d.value,
                      }))
                      .concat(
                        companyCustomData.find((d: any) => d.dataId === id)
                          ? []
                          : [{ dataId: id, value: val }],
                      )
                  : [
                      {
                        dataId: id,
                        value: val,
                      },
                    ];

              handleUpdate('data', newCompanyCustomData, onSuccess);
            }}
          />
        );
      })}
    </div>
  );
};

const CompanyContactProfile: FC = () => {
  const navigate = useNavigate();
  const { data: subscription } = useGetSubscription();
  const { data, isLoading } = useCompanyProfileContext();
  const { showModal, hideModal } = useModal();
  const params = useParams();
  const { data: companyData } = useGetCompanyContactDetails(
    params.id as string,
    {
      include: ['leads', 'tag'],
    },
  );
  const { mutate: deleteCompany, isLoading: deleteLoading } =
    useDeleteCompany();

  const { ACTIONS, PERMISSION_KEYS } = c;

  const { mutate: updateCompany } = useUpdateCompany();

  const handleChangePicture = (photoUrl: string) => {
    updateCompany(
      {
        photoUrl,
        id: companyData?._id,
      },
      {
        onSuccess: hideModal,
      },
    );
  };

  const handleEditCompany = useCallback(() => {
    showModal({
      title: 'Edit Company',
      disableOutsideClick: true,
      content: <UpdateCompanyModal id={data?.id} />,
      size: 'xl',
    });
  }, [data?.id, showModal]);

  const handleCustomFields = useCallback(() => {
    showModal({
      content: (
        <CustomFieldsModal
          source={DATA_SOURCE.COMPANY}
          usedOn={VIEWDATA_TYPE.INFO_PAGE}
        />
      ),
      disableOutsideClick: true,
      resetPadding: true,
      title: 'Custom Fields',
      size: 'xl',
    });
  }, [showModal]);

  const handleDeleteCompany = useCallback(() => {
    showModal({
      title: 'Delete company',
      content: (
        <DeleteItemModal
          onClickDelete={() => {
            deleteCompany({ id: params.id! });
          }}
          text="company"
          isLoading={deleteLoading}
          isDeletable={companyData?.isDeletable}
        />
      ),
      disableOutsideClick: true,
    });
  }, [
    companyData?.isDeletable,
    deleteCompany,
    deleteLoading,
    params.id,
    showModal,
  ]);

  const navOptions = useMemo(
    () => [
      { onClick: handleEditCompany, key: 'EDIT', text: 'Edit' },
      {
        onClick: handleCustomFields,
        key: 'CUSTOM_FIELDS',
        text: 'Custom Fields',
      },
      {
        onClick: handleDeleteCompany,
        key: 'DELETE',
        text: 'Delete',
      },
    ],
    [handleCustomFields, handleDeleteCompany, handleEditCompany],
  );

  return (
    <div
      className={cn(
        'bg-fill-tertiary',
        'w-[280px]',
        'flex-shrink-0',
        'p-[22px] rounded-[6px]',
        'shadow-leapify-sm',
      )}
    >
      <div className="flex justify-between items-center">
        <Button
          onClick={() => navigate(-1)}
          label="Back"
          className="p-0 font-semibold"
          icon={IoChevronBackSharp}
          left
          intent="text"
          size="sm"
        />
        <NavAction
          options={navOptions}
          disabled={subscription?.isExpired}
          label={
            <div className="text-xs flex items-center gap-1">
              Actions <AiOutlineCaretDown />
            </div>
          }
        />
      </div>
      <div
        className={cn(
          'my-2.5',
          'pb-[22px] text-center',
          'border-b-[0.5px] border-b-separator-primary',
        )}
      >
        <div className="mx-auto w-fit">
          <AvatarUploader
            user={data}
            className="lg mb-2.5"
            onSubmit={handleChangePicture}
            isLoading={isLoading}
          />
        </div>
        <h1 className="text-sm font-semibold">{data?.name}</h1>
        <h2 className="max-w-full text-sm font-normal truncate">
          {data?.companyWebsite}
        </h2>
      </div>
      <div
        className={cn(
          'text-content py-2.5 mb-7',
          'flex justify-center gap-x-[20px] items-center',
        )}
      >
        <Can I={ACTIONS.UPDATE} this={PERMISSION_KEYS.COMPANIES}>
          <IconButton role="button" onClick={handleEditCompany}>
            <BsFillPencilFill className="w-[15px] h-[15px]" />
          </IconButton>
        </Can>
      </div>
      <div>
        <Accordion
          label="About this Company"
          arrowLeft
          defaultOpen
          className={'overflow-y-auto scrollbar-thin'}
          content={<CompanyContactDetails id={params.id!} />}
        />
      </div>
    </div>
  );
};

export default CompanyContactProfile;
